import React, { useState, useEffect } from "react";
import { Dialog } from "../../../../../../ui/components/Dialog";
import { Button } from "../../../../../../ui/components/Button";
import { TextField } from "../../../../../../ui/components/TextField";
import { FeatherX, FeatherCheck } from "@subframe/core";
import { PivotView } from "../types";

interface SaveAllTabsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (tabNames: Record<string, string>, registerSetName?: string) => void;
  tabs: PivotView[];
  isSaving?: boolean;
  registerName?: string; // Current register set name (if exists)
  onSetName?: (setName: string) => void | Promise<void>; // Callback to update register name
  fileId?: string; // Required if onSetName is provided
}

export const SaveAllTabsModal: React.FC<SaveAllTabsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  tabs,
  isSaving = false,
  registerName,
  onSetName,
  fileId,
}) => {
  const [tabNames, setTabNames] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [registerSetName, setRegisterSetName] = useState<string>(registerName || "");
  const [hasUserTyped, setHasUserTyped] = useState<boolean>(false);

  // Auto-generate names based on configuration
  const generateSuggestedName = (tab: PivotView): string => {
    const rowGroups = tab.config.rowGroups.map((f) => f.name).join(", ");
    const values = tab.config.values.map((f) => f.name).join(", ");
    
    if (rowGroups && values) {
      return `${values} by ${rowGroups}`;
    } else if (rowGroups) {
      return `Report by ${rowGroups}`;
    } else if (values) {
      return values;
    }
    return tab.name;
  };

  useEffect(() => {
    if (isOpen) {
      // Initialize with suggested names
      const initialNames: Record<string, string> = {};
      tabs.forEach((tab) => {
        initialNames[tab.id] = generateSuggestedName(tab);
      });
      setTabNames(initialNames);
      setErrors({});
      // Only initialize register name if user hasn't typed anything yet
      // This prevents resetting the input when parent component updates registerName
      if (!hasUserTyped) {
        setRegisterSetName(registerName || "");
      }
    } else {
      // Reset when modal closes
      setHasUserTyped(false);
    }
  }, [isOpen, tabs]); // Removed registerName from dependencies to prevent reset

  // Update register name only when modal first opens or when registerName changes and user hasn't typed
  useEffect(() => {
    if (isOpen && !hasUserTyped && registerName) {
      setRegisterSetName(registerName);
    }
  }, [isOpen, registerName, hasUserTyped]);

  const handleNameChange = (tabId: string, name: string) => {
    setTabNames((prev) => ({
      ...prev,
      [tabId]: name,
    }));
    // Clear error for this tab
    if (errors[tabId]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[tabId];
        return updated;
      });
    }
  };

  const handleConfirm = async () => {
    // Validate: at least one tab must have a name
    const hasValidNames = Object.values(tabNames).some((name) => name.trim() !== "");
    
    if (!hasValidNames) {
      alert("Please enter at least one name to save.");
      return;
    }

    // Filter out empty names (tabs user doesn't want to save)
    const validTabNames: Record<string, string> = {};
    Object.entries(tabNames).forEach(([tabId, name]) => {
      if (name.trim()) {
        validTabNames[tabId] = name.trim();
      }
    });

    // Validate: register name is required
    if (!registerSetName.trim()) {
      alert("Please enter a register name.");
      return;
    }

    // Update register set name if provided and onSetName callback exists
    const finalRegisterName = registerSetName.trim();
    if (finalRegisterName && onSetName && fileId) {
      try {
        await onSetName(finalRegisterName);
      } catch (error) {
        console.error("Failed to update register set name:", error);
        // Continue with saving tabs even if register name update fails
      }
    }

    onConfirm(validTabNames, finalRegisterName);
  };

  const tabsToSave = tabs.filter((tab) => tabNames[tab.id]?.trim());
  const newTabsCount = tabsToSave.filter((tab) => !tab.savedViewId || !tab.isSaved).length;
  const updateTabsCount = tabsToSave.filter((tab) => tab.savedViewId && tab.isSaved).length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <Dialog.Content className="min-w-[600px] max-w-[800px]">
        <div className="flex flex-col gap-6 p-6">
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b border-neutral-border">
            <div>
              <h3 className="text-heading-3 font-heading-3 text-default-font mb-1">
                Save & Name Tabs
              </h3>
              <p className="text-body font-body text-subtext-color">
                Name your register set and individual tabs. Already saved tabs can be renamed.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-subtext-color hover:text-default-font transition-colors"
              disabled={isSaving}
            >
              <FeatherX className="w-5 h-5" />
            </button>
          </div>

          {/* Register Name Section */}
          <div className="p-4 rounded-md bg-neutral-50 border border-neutral-border">
            <TextField
              label="Register Name"
              error={!registerSetName.trim() && tabsToSave.length > 0}
              helpText={!registerSetName.trim() && tabsToSave.length > 0 ? "Register name is required" : "Give a name to this set of registers"}
            >
              <TextField.Input
                value={registerSetName}
                onChange={(e) => {
                  setRegisterSetName(e.target.value);
                  setHasUserTyped(true);
                }}
                placeholder="e.g., Monthly Sales Report - Nov 2025"
                disabled={isSaving}
              />
            </TextField>
          </div>

          {/* Tabs List */}
          <div className="flex flex-col gap-4 max-h-[400px] overflow-y-auto">
            {tabs.map((tab) => {
              const hasConfig = tab.config.values.length > 0;
              const isSelected = tabNames[tab.id]?.trim() !== "";
              const isAlreadySaved = tab.savedViewId && tab.isSaved;

              return (
                <div
                  key={tab.id}
                  className={`p-4 rounded-md border ${
                    isSelected
                      ? "border-brand-300 bg-brand-50"
                      : "border-neutral-border bg-neutral-50"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-2">
                      {isSelected ? (
                        <FeatherCheck className="w-5 h-5 text-brand-600" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-neutral-300" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-body-bold font-body-bold text-default-font">
                          {tab.name}
                        </span>
                        {isAlreadySaved && (
                          <span className="text-xs px-2 py-0.5 bg-success-100 text-success-700 rounded">
                            Saved
                          </span>
                        )}
                        {!isAlreadySaved && hasConfig && (
                          <span className="text-xs px-2 py-0.5 bg-warning-100 text-warning-700 rounded">
                            New
                          </span>
                        )}
                        {!hasConfig && (
                          <span className="text-caption font-caption text-error-600">
                            (No values configured)
                          </span>
                        )}
                      </div>
                      <TextField
                        label={isAlreadySaved ? "Rename to" : "Save as"}
                        error={!!errors[tab.id]}
                        helpText={errors[tab.id] || (hasConfig ? (isAlreadySaved ? "Update the name or leave as is" : "") : "Add values to save this tab")}
                      >
                        <TextField.Input
                          value={tabNames[tab.id] || ""}
                          onChange={(e) => handleNameChange(tab.id, e.target.value)}
                          placeholder={isAlreadySaved ? "Enter new name or keep current" : "Enter name (leave empty to skip)"}
                          disabled={!hasConfig || isSaving}
                        />
                      </TextField>
                      {hasConfig && (
                        <div className="mt-2 text-xs text-subtext-color">
                          <span className="font-body-bold">Config: </span>
                          {tab.config.rowGroups.length > 0 && (
                            <span>Rows: {tab.config.rowGroups.map((f) => f.name).join(", ")}</span>
                          )}
                          {tab.config.values.length > 0 && (
                            <span className="ml-2">
                              Values: {tab.config.values.map((f) => f.name).join(", ")}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="px-4 py-3 rounded-md bg-neutral-50 border border-neutral-border">
            <p className="text-body font-body text-default-font">
              <span className="font-body-bold">{tabsToSave.length}</span> of{" "}
              <span className="font-body-bold">{tabs.length}</span> tabs will be saved
              {newTabsCount > 0 && updateTabsCount > 0 && (
                <span className="text-subtext-color ml-2">
                  ({newTabsCount} new, {updateTabsCount} update)
                </span>
              )}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t border-neutral-border">
            <Button variant="neutral-tertiary" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              onClick={handleConfirm}
              disabled={isSaving || tabsToSave.length === 0 || !registerSetName.trim()}
            >
              {isSaving
                ? `Saving ${tabsToSave.length} tabs...`
                : `Save ${tabsToSave.length} Tab${tabsToSave.length !== 1 ? "s" : ""}`}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

